const REGISTRY_BASE_URL = 'https://coconet.trellixor.com';
const API_NAME = 'trellixo';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const FALLBACK_URL = 'https://trellixo-api-prod.ambitiousrock-947c773c.australiaeast.azurecontainerapps.io';

class ApiRegistryService {
  constructor() {
    this.cached = null;
    this.cachedAt = null;
    this.failedEndpoints = new Set();
    this.roundRobinIndex = 0;
    this.inFlightFetch = null;
  }

  get currentBaseUrl() {
    if (process.env.NODE_ENV === 'development') {
      return process.env.REACT_APP_API_URL || 'http://localhost:8080';
    }
    if (!this.cached) {
      this.coalescedFetch().catch(() => {});
      return FALLBACK_URL;
    }

    // Background refresh when cache is stale
    if (!this.isCacheValid) {
      this.coalescedFetch().catch(() => {});
    }

    return this.selectEndpoint(this.cached);
  }

  async initialize() {
    if (process.env.NODE_ENV === 'development') return;
    try {
      await this.coalescedFetch();
    } catch (e) {
      console.warn('API Registry prefetch failed:', e);
    }
  }

  markEndpointFailed(url) {
    this.failedEndpoints.add(url);
    this.cachedAt = null;
    this.coalescedFetch().catch(() => {});
  }

  get isCacheValid() {
    return (
      this.cached !== null &&
      this.cachedAt !== null &&
      Date.now() - this.cachedAt < CACHE_DURATION_MS
    );
  }

  selectEndpoint(response) {
    // 1. Recommended (if not failed)
    if (!this.failedEndpoints.has(response.recommendedEndpoint)) {
      return response.recommendedEndpoint;
    }
    // 2. Round-robin healthy endpoints
    const available = response.endpoints.filter(
      (ep) => ep.healthy && !this.failedEndpoints.has(ep.url)
    );
    if (available.length > 0) {
      const index = this.roundRobinIndex % available.length;
      this.roundRobinIndex++;
      return available[index].url;
    }
    // 3. Fallback
    return response.fallback;
  }

  async coalescedFetch() {
    if (this.inFlightFetch) return this.inFlightFetch;

    this.inFlightFetch = (async () => {
      try {
        const response = await this.fetchRegistry();
        this.cached = response;
        this.cachedAt = Date.now();
        this.failedEndpoints.clear();
        return response;
      } finally {
        this.inFlightFetch = null;
      }
    })();

    return this.inFlightFetch;
  }

  async fetchRegistry() {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);

    try {
      const res = await fetch(`${REGISTRY_BASE_URL}/${API_NAME}`, {
        signal: controller.signal,
      });
      if (!res.ok) {
        throw new Error(`Registry returned ${res.status}`);
      }
      return await res.json();
    } finally {
      clearTimeout(timeout);
    }
  }
}

export const apiRegistry = new ApiRegistryService();

export function isEndpointError(status) {
  return status === 502 || status === 503 || status === 504;
}
